import React from 'react'
import { CustomTooltipProps, DonutChart, Text } from '@tremor/react'

import { DONUT_CHART_MIN_VALUE } from 'consts/charts'

import { formatDashboardNumber } from '../utils/formatDashboardNumber'

const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }
const arrowStyle = {
  ...montserratStyle,
  fontSize: 30,
  fontWeight: 500,
  marginTop: -5,
}

export interface DonutChartWithStatsProps {
  // eslint-disable-next-line react/require-default-props
  title?: string
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  data: Array<{
    name: string
    value: number
    color?: string
    diff?: number
    isPositive?: boolean
    prev?: number
    comparisonStore?: string
    quantity?: number
  }>
  showComparison?: boolean
  className?: string
  style?: React.CSSProperties
  chartTitle?: string
  customTooltip?: React.ComponentType<CustomTooltipProps>
}

export const DonutChartWithStats: React.FC<DonutChartWithStatsProps> = ({
  title,
  data,
  showComparison = false,
  className = '',
  style = {},
  chartTitle,
  customTooltip,
}) => {
  return (
    <div className={`p-6 bg-white ${className}`} style={style}>
      <Text className="text-lg font-bold mb-4" style={montserratStyle}>
        {title}
      </Text>
      <div className="flex flex-row items-center justify-center">
        <div
          className="w-80 h-80 flex items-center justify-center"
          style={{ minWidth: 270, minHeight: 270 }}
        >
          <DonutChart
            showLabel={false}
            data={data}
            category="value"
            index="name"
            colors={data.map(d => d.color || '#ccc')}
            showTooltip
            showAnimation
            {...(customTooltip && { customTooltip })}
          />
        </div>
        <div className="flex flex-col gap-4 ml-6 w-full justify-center">
          {chartTitle && (
            <div className="text-base font-bold mb-2" style={montserratStyle}>
              {chartTitle}
            </div>
          )}
          {data.map(item => (
            <div key={item.name} className="flex items-center gap-2">
              <span
                className={`bg-${item.color}-500`}
                style={{
                  width: 8,
                  height: 76,
                  borderRadius: 4,
                  display: 'inline-block',
                }}
              />
              <div>
                <span className="text-xl font-extrabold" style={montserratStyle}>
                  {item.value <= DONUT_CHART_MIN_VALUE ? 0 : item.value}%
                </span>
                <div className="text-base" style={montserratStyle}>
                  {item.name}
                </div>
                {item.diff && (
                  <div className="flex items-center gap-2">
                    <span
                      className="flex items-center font-bold rounded-lg px-2 text-base"
                      style={{
                        ...montserratStyle,
                        background: item.isPositive ? '#C1FD96CC' : '#FFDBDB',
                        color: item.isPositive ? '#1E1E1E' : '#E53935',
                      }}
                    >
                      {item.isPositive ? (
                        <>
                          <span style={arrowStyle}>↑</span>
                          <span>{`${item.diff}%`}</span>
                        </>
                      ) : (
                        <>
                          <span style={arrowStyle}>↓</span>
                          <span>{`${Math.abs(item.diff || 0)}%`}</span>
                        </>
                      )}
                    </span>
                    {showComparison && item.comparisonStore && (
                      <span
                        className="text-gray-600 text-base whitespace-nowrap"
                        style={montserratStyle}
                      >
                        (
                        {typeof item.prev === 'number'
                          ? formatDashboardNumber(item.prev)
                          : item.prev}{' '}
                        de la sucursal {item.comparisonStore})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

DonutChartWithStats.defaultProps = {
  className: '',
  style: {},
  chartTitle: undefined,
  showComparison: false,
  customTooltip: undefined,
}

export default DonutChartWithStats
